<template>
  <VDataGridV2
    entity-type="offer"
    :loader="dataLoader"
    paginated
    :selectable="selectable"
    @row:selected="onRowClick"

    @row:dblclick="openOfferCard"

    v-model:selected-rows="selectedRows"
  >
    <VDataGridColumnV2
      v-if="isColumnVisible('id')"
      id="id"
      label="№"
      width="70px"
      content-align="center"
    >
      <template #default="{ row }">
        <VLink @click="openOfferCard(row)">{{ row.id }}</VLink>

        <VBadge v-if="row.isPublished">Опубликован</VBadge>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('isOutdated')"
      id="isOutdated"
      width="60px"
      label="Актуально"
      content-align="center"
      filterable
      filter-type="select"
      filter-send-name="isOutdated"
      :filter-props="{ options: isOutdatedList }"
      filter-identity="isOutdated"
      :filter-default-value="isOutdatedList.find(item => item.id === 2)"
    >
      <template #default="{ row }">
        {{ row.isActual ? 'Да' : 'Нет' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('seller')"
      id="seller"
      width="150px"
      content-class="ellipsis"
      label="Поставщик"
      filterable
      filter-type="select"
      filter-send-name="sellerId"
      :filter-props="{ searchFunction: PartnersService.searchSellers, placeholder: 'Название партнера', clearable: true }"
    >
      <template #default="{ row }">
        <div :title="row.sellerCompany.name"
             class="ellipsis">
          {{ row.sellerCompany.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('buyer')"
      id="buyer"
      width="140px"
      content-class="ellipsis"
      label="Покупатель"
      filterable
      filter-type="select"
      filter-send-name="buyerId"
      :filter-props="{ searchFunction: PartnersService.searchBuyers, placeholder: 'Название партнера', clearable: true }"
    >
      <template #default="{ row }">
        <div class="ellipsis"
             :title="row.buyer.name">
          {{ row.buyer.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('name')"
      id="name"
      label="Сырьё"
      content-class="ellipsis"
      width="300px"
      filterable
      filter-type="text"
      filter-send-name="pattern"
      :filter-props="{ placeholder: 'Название категоии', clearable: true }"
    >
      <template #default="{ row }">
        <div
          :title="row.productCategory.name"
          class="ellipsis">{{ row.productCategory.name }}
        </div>
        <div
          class="description ellipsis"
          :title="row.manufacturer.name"
        >
          {{ row.manufacturer.name }}
        </div>
        <div class="description">
          Годен до {{ row.productExpirationDate }}
        </div>

        <VBadges>
          <template v-if="row.isAcceptingPreorder">
            <VBadge>
              Доступен предзаказ от {{ row.preorderMinQty || '--' }} {{ row.unit?.name }}
            </VBadge>
            <VBadge>
              Страховой запас:
              <template v-if="row.preorderMonthlyReserve === null">нет</template>
              <template v-else>{{ row.preorderMonthlyReserve || '--' }} {{ row.unit?.name }}
              </template>
            </VBadge>
          </template>
          <VBadge v-if="row.isSamples">
            Образец
          </VBadge>
          <VBadge v-if="row.updatedAt">
            Актуален до {{ row.relevantTo.format() }}
          </VBadge>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('deliveryDays')"
      id="deliveryDays"
      width="190px"
    >
      <template #label>
        Интервал получения<br>
        <div class="description">
          Кто осуществляет доставку
        </div>
      </template>
      <template #default="{ row }">
        <div class="title">
          {{ row.receiptPeriod?.toString({prefix: 'с', middle: 'по'}) ?? '--' }}
        </div>
        <div class="description">
          Доставляется {{ row.deliveryType.name.toLowerCase() }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('qty')"
      id="qty"
      label="Доступно"
      width="150px"
      content-align="right"
    >
      <template #default="{ row }">
        <div class="title">
          {{ row.qtyLeft.format(row.unit) }}
        </div>
        <div class="description">
          Упаковка: {{ row.package?.name || 'не указано' }}
        </div>
        <div class="description">
          Фасовка: {{ row.packageVolume?.format(row.unit) || 'не указано' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('price')"
      id="price"
      width="160px"
      content-align="right"
      label="Цена + доставка, с НДС"
      header-class="nowrap"
    >
      <template #default="{ row }">
        <template v-if="row.priceFullBuyer">
          {{
              row.priceFullBuyer.formatInDisplayCurrency(row.offerCurrencyCode, row.getDateForCurrencyRate())
            }}

          <div
            v-if="row.offerCurrencyCode !== $core.displayedCurrencyCode"
            class="description"
          >
            Пересчитано из {{ row.priceFullBuyer.format(row.offerCurrencyCode, row.unit) }}
          </div>
          <div v-else class="description">Валюта предложения</div>
        </template>
        <template v-else>--</template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('isPublished') && $core.currentUser?.isAdmin"
      id="isPublished"
      width="70px"
      label="Опубликовано"
      content-align="center"
      filterable
      filter-type="select"
      filter-send-name="isPublished"
      :filter-props="{ options: isPublishedList }"
      filter-identity="isPublished"
    >
      <template #default="{ row }">
        {{ row.isPublished ? 'Да' : 'Нет' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('action')"
      id="action"
      width="55px"
      content-align="left"
    >
      <template #default="{ row }">
        <VButton material="add_shopping_cart"
                 @click="OffersService.showOrderCreateDialogMinimized(row)"/>
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2>
      <VButton theme="primary" @click="createOffer">
        Создать предложение
      </VButton>
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<script lang="ts">

import type Tender from '@/domains/Tenders/Tender';
import DataGridLoader from '@/lib/components/VDataGridV2/DataGridLoader';
import OffersService from "@/domains/Offers/OffersService";
import Offer from "@/domains/Offers/Offer";
import PartnersService from "@/domains/Partners/PartnersService";
import Core from "@/core/Core";

const isPublishedList = [
  {id: 1, isPublished: true, name: 'Да'},
  {id: 2, isPublished: false, name: 'Нет'},
];

/** NotaBene!!! лейблы инвертированы, так как на фронте
 * колонка называется Актуально, те есть isOutdated = false
 */
const isOutdatedList = [
  {id: 1, isOutdated: true, name: 'Нет'},
  {id: 2, isOutdated: false, name: 'Да'},
];

export default defineComponent({
  name: 'OffersRegistry',

  props: {
    /** Параметры запроса предложений (фильтры на уровне API). */
    params: {
      type: Object as PropType<Parameters<typeof OffersService.getListPaginated>[0]>,
      default: null,
    },

    /** Скрытые колонки реестра по id. */
    hiddenColumns: {
      type: Array as PropType<string[]>,
      default: () => [],
    },

    /**
     * Режим выбора: клик по строке отдаёт предложение наружу. Выключен —
     * реестр работает как обычный список, клик ничего не делает.
     */
    selectable: {
      type: Boolean,
      default: false,
    },

    /**
     * Тендер, из которого открыли реестр. Новое предложение создаётся
     * под него — карточка сама заполнит поля по тендеру.
     */
    tender: {
      type: Object as PropType<Tender | null>,
      default: null,
    },
  },

  emits: ['tenderBid:created'],

  setup() {
    return {
      OffersService,
      Offer,
      PartnersService,
      isPublishedList,
      isOutdatedList,
    }
  },

  data() {
    return {
      dataLoader: new DataGridLoader(OffersService.getListPaginated)
        .useExtraParams({
          ...(Core.currentUser?.isAdmin ? {isAdmin: true} : {}),
          ...this.params,
        }),
      selectedRows: [],
    };
  },

  computed: {
    hiddenColumnsSet(): Set<string> {
      return new Set(this.hiddenColumns);
    },

  },

  methods: {
    async openOfferCard(row: Offer): Promise<void> {
      await OffersService.showOfferCard(row);
      this.dataLoader.refresh();
    },

    async onRowClick(offer: Offer): void {
      if (this.selectable === true) {

        // Если передан тендер, то значит мы создаем ставку по тендеру
        if (this.tender) {
          const tenderBidWasCreated = await this.$core.dialogs.TenderBidCard.showAsPromise({
              offerId: offer.id,
              tender: this.tender,
            }
          )

          if (tenderBidWasCreated) this.$emit('tenderBid:created')
          else {
            // Ситуация: пользователь мискликнул, закрыл диалог создания ставки,
            // в такой ситуации таблица должна изменить состояние и отжать предыдущий выбор
            this.selectedRows = [];
          }
        }
      }
    },

    /** Подходящего предложения в списке нет — заводим новое. */
    async createOffer(): Promise<void> {
      await this.$core.dialogs.OfferEditDialog.showAsPromise({
        tender: this.tender,
      });

      this.dataLoader.refresh();
    },

    isColumnVisible(columnId: string): boolean {
      return this.hiddenColumnsSet.has(columnId) === false;
    },
  },
});
</script>
